import { useEffect, useState } from "react";
import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { LayoutDashboard, Star, Image as ImageIcon, FileText, LogOut, ExternalLink, Plus, Menu, X, ShieldCheck } from "lucide-react";

const navGroups = [
  {
    label: "Core",
    items: [
      { label: "Dashboard", path: "/admin", icon: LayoutDashboard },
      { label: "Reviews", path: "/admin/reviews", icon: Star },
    ],
  },
  {
    label: "CMS Engine",
    items: [
      { label: "Smile Gallery", path: "/admin/gallery", icon: ImageIcon },
      { label: "Blog Posts", path: "/admin/blog", icon: FileText },
    ],
  },
];

const AdminLayout = () => {
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("Admin");
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) navigate("/admin/login");
      else setEmail((data.session.user.email || "Admin").split("@")[0]);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      if (!session) navigate("/admin/login");
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // Close mobile sheet on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  if (loading) return <div className="min-h-dvh flex items-center justify-center">Loading…</div>;

  const isActive = (path: string) =>
    path === "/admin" ? location.pathname === "/admin" : location.pathname.startsWith(path);

  const Sidebar = (
    <aside className="w-[260px] shrink-0 bg-white border-r border-[hsl(var(--admin-border))] flex flex-col h-dvh sticky top-0">
      {/* Brand */}
      <div className="px-6 pt-7 pb-6 flex flex-col items-center text-center border-b border-[hsl(var(--admin-border))]">
        <div
          className="h-20 w-20 rounded-full flex items-center justify-center text-white font-heading text-2xl font-semibold mb-3"
          style={{ background: "linear-gradient(135deg, hsl(var(--admin-teal)), hsl(var(--admin-navy-deep)))" }}
        >
          M
        </div>
        <p className="font-heading text-[11px] tracking-[0.3em] text-[hsl(var(--admin-muted))]">MANAGEMENT</p>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-4 py-5 space-y-6">
        {navGroups.map((g) => (
          <div key={g.label}>
            <p className="admin-eyebrow px-3 mb-2">{g.label}</p>
            <div className="space-y-1">
              {g.items.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      active
                        ? "admin-nav-active"
                        : "text-[hsl(var(--admin-ink))] hover:bg-[hsl(var(--admin-bg))]"
                    }`}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-4 py-4 border-t border-[hsl(var(--admin-border))] space-y-1">
        <p className="admin-eyebrow px-3 mb-2">System</p>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-[hsl(var(--admin-ink))] hover:bg-red-50 hover:text-red-600 w-full transition-colors"
        >
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>
    </aside>
  );

  return (
    <div className="theme-admin min-h-dvh flex">
      {/* Desktop sidebar */}
      <div className="hidden lg:block">{Sidebar}</div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="relative z-10 animate-in slide-in-from-left">{Sidebar}</div>
        </div>
      )}

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-[hsl(var(--admin-bg))]/85 backdrop-blur-md border-b border-[hsl(var(--admin-border))]">
          <div className="flex items-center justify-between gap-3 px-4 lg:px-8 py-4">
            <div className="flex items-center gap-2">
              <button
                className="lg:hidden h-9 w-9 rounded-lg border border-[hsl(var(--admin-border))] bg-white flex items-center justify-center"
                onClick={() => setMobileOpen((o) => !o)}
              >
                {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </button>
              <Link
                to="/admin/blog"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--admin-teal))]/10 text-[hsl(var(--admin-teal-deep))] text-sm font-medium hover:bg-[hsl(var(--admin-teal))]/15 transition"
              >
                <Plus className="h-4 w-4" /> New Blog
              </Link>
              <a
                href="/"
                target="_blank"
                rel="noopener"
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[hsl(var(--admin-border))] text-sm font-medium hover:border-[hsl(var(--admin-teal))] transition"
              >
                <ExternalLink className="h-4 w-4" /> View Site
              </a>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[hsl(var(--admin-border))]">
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[hsl(var(--admin-teal))] to-[hsl(var(--admin-navy))] text-white flex items-center justify-center text-xs font-semibold uppercase">
                  {email.slice(0, 1)}
                </div>
                <span className="text-sm font-medium capitalize hidden sm:inline">{email}</span>
              </div>
              <button
                onClick={handleLogout}
                className="h-9 w-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center transition"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto px-4 lg:px-8 py-6 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
